import { useRef, useState } from "react";
import { connectDevice, checkDevice, evmGetAddress } from "@gardio/device";
import type { DeviceAccount } from "@gardio/device";
import {
  defaultTokenAddress,
  getTokenBalanceOf,
} from "@gardio/contracts/polygon";
import { useSettlementToken } from "features/tokens/useSettlementToken";
import type { Network } from "@gardio/contracts/polygon";
import { apiErrorMessage } from "utils/errors/parseApiError";

export interface AccountWithBalance extends DeviceAccount {
  balance: number | undefined;
}

const BATCH = 5;

export function useAccountFetcher(network: Network) {
  // Read the balance in the SELECTED token. Without this the picker showed a VIP figure under
  // whatever label the token chip was on.
  const { param: selectedTokenAddress } = useSettlementToken();
  const [accounts, setAccounts] = useState<AccountWithBalance[]>([]);
  const [nextIndex, setNextIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const fetchingRef = useRef(false);

  const fetchBatch = async () => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      if (!checkDevice()) await connectDevice();

      const derived = await evmGetAddress(nextIndex, BATCH);
      const withBalances: AccountWithBalance[] = await Promise.all(
        derived.map(async (acc: DeviceAccount) => {
          try {
            const balance = await getTokenBalanceOf(
              acc.address,
              network,
              selectedTokenAddress ?? defaultTokenAddress(network),
            );
            return { ...acc, balance };
          } catch {
            return { ...acc, balance: undefined };
          }
        }),
      );

      setAccounts((prev) => [...prev, ...withBalances]);
      setNextIndex((prev) => prev + BATCH);
      setHasMore(derived.length === BATCH);
    } catch (err: any) {
      setError(
        apiErrorMessage(
          err,
          "Failed to fetch accounts. Ensure your Keyfob is connected.",
        ),
      );
    } finally {
      fetchingRef.current = false;
      setLoading(false);
    }
  };

  const reset = () => {
    setAccounts([]);
    setNextIndex(0);
    setHasMore(true);
    setError(null);
    fetchingRef.current = false;
  };

  return {
    accounts,
    loading,
    error,
    hasMore,
    fetchBatch,
    reset,
    clearError: () => setError(null),
  };
}
